import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { Link, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import Select from 'react-select';
import moment from 'moment';
import { confirmAlert } from 'react-confirm-alert';
import 'react-confirm-alert/src/react-confirm-alert.css';
import * as sessionNotesService from '../../services/sessionNotes';
import * as franchiseService from '../../services/franchise';
import * as centreService from '../../services/centre';
import { base64Decode } from '../../helpers/base64url';

interface StudentNote {
  session_note_student_id: number;
  session_note_id: number;
  student_id: number;
  student_name: string;
  subject_id: number;
  subject_name: string;
  attended: string;
  homework_status: string; // 'NA', 'Completed', 'Completed and Marked', 'Not Completed'
  session_note: string;
  status: string;
  franchise_name: string;
  centre_name: string;
  session_date: string;
  created_by_name?: string;
  selected?: boolean;
  comment?: string;
  edited_session_note?: string; // Track edited session note
}

const ReviewSessionNotes: React.FC = () => {
  const { encodedData } = useParams<{ encodedData?: string }>();
  const canAccess = useSelector((state: any) => state.auth.permissions?.['session-notes']);
  
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [franchises, setFranchises] = useState<any[]>([]);
  const [centres, setCentres] = useState<any[]>([]);
  const [subjects, setSubjects] = useState<any[]>([]);
  const [sessionNotes, setSessionNotes] = useState<StudentNote[]>([]);
  const [generalComment, setGeneralComment] = useState(''); // Single comment for all unselected notes
  const [filters, setFilters] = useState({
    session_date: '',
    franchise_id: '',
    centre_id: '',
    subject_id: ''
  });

  // Decode URL parameters if provided
  useEffect(() => {
    if (encodedData) {
      try {
        const decoded = base64Decode(encodedData);
        const data = JSON.parse(decoded);
        setFilters(prev => ({
          ...prev,
          session_date: data.date || '',
          franchise_id: data.franchise_id?.toString() || '',
          centre_id: data.centre_id?.toString() || ''
        }));
      } catch (error) {
        console.error('Failed to decode URL parameters:', error);
      }
    }
  }, [encodedData]);

  useEffect(() => {
    fetchFranchises();
  }, []);

  useEffect(() => {
    // Auto-select franchise if only one is available
    if (franchises.length === 1 && !filters.franchise_id) {
      setFilters(prev => ({
        ...prev,
        franchise_id: franchises[0].id.toString()
      }));
    }
  }, [franchises]);

  useEffect(() => {
    // Auto-select centre if only one is available
    if (centres.length === 1 && !filters.centre_id) {
      setFilters(prev => ({
        ...prev,
        centre_id: centres[0].id.toString()
      }));
    }
  }, [centres]);

  useEffect(() => {
    // Auto-fetch session notes when filters are complete
    if (filters.session_date && filters.franchise_id) {
      fetchSessionNotes();
    }
  }, [filters.session_date, filters.franchise_id, filters.centre_id, filters.subject_id]);

  useEffect(() => {
    if (filters.franchise_id) {
      fetchCentres(parseInt(filters.franchise_id));
      fetchSubjects(parseInt(filters.franchise_id));
    } else {
      setCentres([]);
      setSubjects([]);
      setFilters(prev => ({ ...prev, centre_id: '', subject_id: '' }));
    }
  }, [filters.franchise_id]);

  const fetchFranchises = async () => {
    try {
      const response = await franchiseService.getAllActiveFranchise();
      if (response?.success) {
        setFranchises(response.collections || []);
      }
    } catch (error) {
      toast.error('Failed to fetch franchises');
    }
  };

  const fetchCentres = async (franchiseId: number) => {
    try {
      const response = await centreService.getCentresByFranchiseId(franchiseId);
      if (response?.success) {
        setCentres(response.collections || []);
      }
    } catch (error) {
      toast.error('Failed to fetch centres');
    }
  };

  const fetchSubjects = async (franchiseId: number) => {
    try {
      const response = await franchiseService.getSubjectsByFranchise(franchiseId);
      if (response?.success) {
        const activeSubjects = response?.collections?.filter((sub: any) => sub.status === 'active' && sub.subject_id) || [];
        setSubjects(activeSubjects);
      }
    } catch (error) {
      toast.error('Failed to fetch subjects');
    }
  };

  const fetchSessionNotes = async () => {
    if (!filters.session_date) {
      toast.error('Please select a session date');
      return;
    }

    if (!filters.franchise_id) {
      toast.error('Please select a franchise');
      return;
    }

    setLoading(true);
    
    try {
      const apiFilters: any = {
        sessionDate: filters.session_date,
        franchiseId: parseInt(filters.franchise_id),
        sessionStatus: 'review_pending'
      };

      if (filters.centre_id) {
        apiFilters.centreId = parseInt(filters.centre_id);
      }

      if (filters.subject_id) {
        apiFilters.subjectId = parseInt(filters.subject_id);
      }

      const response = await sessionNotesService.getSessionNoteDetails(apiFilters);
      
      if (response?.success) {
        const notes = response.collections || [];
        setSessionNotes(notes.map((note: any) => ({
          session_note_student_id: note.id,
          session_note_id: note.session_id,
          student_id: note.student_id,
          student_name: note.student_name,
          subject_id: note.subject_id,
          subject_name: note.subject_name,
          attended: note.attended,
          homework_status: note.homework_status,
          session_note: note.session_note,
          status: note.status,
          franchise_name: note.franchise_name,
          centre_name: note.centre_name,
          session_date: note.session_date,
          created_by_name: note.created_by_name,
          selected: false,
          comment: ''
        })));
        
        //if (notes.length === 0) {
          // toast.info('No session notes pending review for the selected criteria');
        //}
      } else {
        toast.error(response?.message || 'Failed to fetch session notes');
        setSessionNotes([]);
      }
    } catch (error) {
      toast.error('Failed to fetch session notes');
      setSessionNotes([]);
    } finally {
      setLoading(false);
    }
  };

  const handleSelectAll = (checked: boolean) => {
    setSessionNotes(prev =>
      prev.map(note => ({ ...note, selected: checked }))
    );
  };

  const handleSelectNote = (sessionNoteStudentId: number, checked: boolean) => {
    setSessionNotes(prev =>
      prev.map(note =>
        note.session_note_student_id === sessionNoteStudentId
          ? { ...note, selected: checked }
          : note
      )
    );
  };

  const handleSessionNoteChange = (sessionNoteStudentId: number, value: string) => {
    setSessionNotes(prev =>
      prev.map(note =>
        note.session_note_student_id === sessionNoteStudentId
          ? { ...note, edited_session_note: value }
          : note
      )
    );
  };

  const handleGeneralCommentChange = (comment: string) => {
    setGeneralComment(comment);
  };

  const getHomeworkStatus = (homework_status: string) => {
    if (homework_status === 'completed_and_marked') {
      return { text: 'Completed and Marked', class: 'badge-success' };
    } else if (homework_status === 'completed') {
      return { text: 'Completed', class: 'badge-info' };
    } else if (homework_status === 'not_completed') {
      return { text: 'Not Completed', class: 'badge-danger' };
    }
    return { text: 'N/A', class: 'badge-secondary' };
  };

  const handleSubmit = async () => {
    const selectedNotes = sessionNotes.filter(note => note.selected);
    const unselectedNotes = sessionNotes.filter(note => !note.selected);

    if (selectedNotes.length === 0 && unselectedNotes.length === 0) {
      toast.error('No session notes to process');
      return;
    }

    // Validate that there's a general comment if there are unselected notes
    if (unselectedNotes.length > 0 && (!generalComment || generalComment.trim() === '')) {
      toast.error('Please provide a comment for session notes that are not selected for publishing');
      return;
    }

    setSubmitting(true);
    
    try {
      // Collect edited session notes
      const editedNotes = sessionNotes
        .filter(note => note.edited_session_note !== undefined && note.edited_session_note !== note.session_note)
        .map(note => ({
          session_note_student_id: note.session_note_student_id,
          session_note: note.edited_session_note
        }));

      // Prepare the payload for bulk status update
      const payload: any = {
        session_note_publish_ids: selectedNotes.map(note => note.session_note_student_id),
        session_note_draft_ids: unselectedNotes.map(note => note.session_note_student_id),
        review_comment: generalComment || ''
      };

      // Add edited session notes if any
      if (editedNotes.length > 0) {
        payload.session_notes_to_update = editedNotes;
      }

      const response = await sessionNotesService.bulkUpdateSessionNoteStatus(payload);
      
      if (response?.success) {
        const totalProcessed = selectedNotes.length + unselectedNotes.length;
        toast.success(
          `Successfully processed ${totalProcessed} session note(s): ${selectedNotes.length} published, ${unselectedNotes.length} sent back to draft`
        );
        
        // Clear the list and comment, then refresh
        setSessionNotes([]);
        setGeneralComment('');
        fetchSessionNotes();
      } else {
        toast.error(response?.message || 'Failed to process session notes');
      }
    } catch (error) {
      toast.error('Failed to process session notes');
    } finally {
      setSubmitting(false);
    }
  };

  const isAllSelected = sessionNotes.length > 0 && sessionNotes.every(note => note.selected);
  const isSomeSelected = sessionNotes.some(note => note.selected) && !isAllSelected;

  if (!canAccess?.review) {
    return (
      <div className="">
        <section className="content">
          <div className="container-fluid">
            <div className="alert alert-warning">
              You do not have permission to review session notes.
            </div>
          </div>
        </section>
      </div>
    );
  }

  return (
    <div className="">
      <section className="content-header">
        <div className="container-fluid">
          <div className="row mb-2">
            <div className="col-sm-6">
              <h1>Review Session Notes</h1>
            </div>
            <div className="col-sm-6">
              <ol className="breadcrumb float-sm-right">
                <li className="breadcrumb-item">
                  <Link to="/">Home</Link>
                </li>
                <li className="breadcrumb-item">
                  <Link to="/session-notes">Session Notes</Link>
                </li>
                <li className="breadcrumb-item active">Review</li>
              </ol>
            </div>
          </div>
        </div>
      </section>

      <section className="content">
        <div className="container-fluid">
          {/* Filters */}
          <div className="card">
            <div className="card-header">
              <h3 className="card-title">Filter Session Notes</h3>
              <div className="card-tools">
                {filters.session_date && (
                  <span className="badge badge-info mr-2">
                    <i className="fas fa-info-circle mr-1"></i>
                    Reviewing session notes for {moment(filters.session_date).format('DD/MM/YYYY')}
                  </span>
                )}
                <Link to="/session-notes" className="btn btn-sm btn-default">
                  <i className="fas fa-arrow-left mr-1"></i>
                  Back to List
                </Link>
              </div>
            </div>
            <div className="card-body">
              {/* Info text for selected franchise and centre */}
              {franchises.length === 1 && centres.length === 1 && filters.franchise_id && filters.centre_id && (
                <div className="mb-3 p-2" style={{ fontSize: '14px', color: '#17a2b8', backgroundColor: '#e7f6f8', borderRadius: '4px' }}>
                  <i className="fas fa-info-circle mr-1"></i>
                  <strong>Franchise:</strong> {franchises.find(f => f.id.toString() === filters.franchise_id)?.name}
                  <span className="mx-2">•</span>
                  <strong>Centre:</strong> {centres.find(c => c.id.toString() === filters.centre_id)?.name}
                </div>
              )}
              {franchises.length === 1 && centres.length > 1 && filters.franchise_id && (
                <div className="mb-3 p-2" style={{ fontSize: '14px', color: '#17a2b8', backgroundColor: '#e7f6f8', borderRadius: '4px' }}>
                  <i className="fas fa-info-circle mr-1"></i>
                  <strong>Franchise:</strong> {franchises.find(f => f.id.toString() === filters.franchise_id)?.name}
                </div>
              )}
              {franchises.length > 1 && centres.length === 1 && filters.franchise_id && filters.centre_id && (
                <div className="mb-3 p-2" style={{ fontSize: '14px', color: '#17a2b8', backgroundColor: '#e7f6f8', borderRadius: '4px' }}>
                  <i className="fas fa-info-circle mr-1"></i>
                  <strong>Centre:</strong> {centres.find(c => c.id.toString() === filters.centre_id)?.name}
                </div>
              )}

              <div className="row">
                <div className={
                  franchises.length === 1 && centres.length === 1 ? "col-md-4" :
                  franchises.length > 1 && centres.length > 1 ? "col-md-3" :
                  franchises.length > 1 || centres.length > 1 ? "col-md-3" : "col-md-4"
                }>
                  <div className="form-group">
                    <label>
                      Session Date <span className="text-danger">*</span>
                    </label>
                    <input
                      type="date"
                      className="form-control"
                      value={filters.session_date}
                      onChange={(e) => setFilters(prev => ({ ...prev, session_date: e.target.value }))}
                      max={new Date().toISOString().split('T')[0]}
                    />
                  </div>
                </div>

                {franchises.length > 1 && (
                  <div className="col-md-3">
                    <div className="form-group">
                      <label>Franchise</label>
                      <Select
                        placeholder="Select Franchise"
                        options={franchises}
                        getOptionValue={(option) => `${option['id']}`}
                        getOptionLabel={(option) => option['name']}
                        value={franchises.find(f => f.id.toString() === filters.franchise_id) || null}
                        onChange={(option) => {
                          setFilters(prev => ({
                            ...prev,
                            franchise_id: option ? option.id.toString() : '',
                            centre_id: '',
                            subject_id: ''
                          }));
                          // Clear session notes when franchise changes
                          setSessionNotes([]);
                        }}
                        isClearable={true}
                        isSearchable={true}
                      />
                    </div>
                  </div>
                )}

                {centres.length > 1 && (
                  <div className={
                    franchises.length === 1 && centres.length === 1 ? "col-md-4" :
                    franchises.length > 1 && centres.length > 1 ? "col-md-2" :
                    franchises.length > 1 || centres.length > 1 ? "col-md-3" : "col-md-4"
                  }>
                    <div className="form-group">
                      <label>Centre</label>
                      <select
                        className="form-control"
                        value={filters.centre_id}
                        onChange={(e) => setFilters(prev => ({ ...prev, centre_id: e.target.value }))}
                        disabled={!filters.franchise_id}
                      >
                        <option value="">All Centres</option>
                        {centres.map(centre => (
                          <option key={centre.id} value={centre.id.toString()}>
                            {centre.name}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                )}

                <div className={
                  franchises.length === 1 && centres.length === 1 ? "col-md-4" :
                  franchises.length > 1 && centres.length > 1 ? "col-md-2" :
                  franchises.length > 1 || centres.length > 1 ? "col-md-3" : "col-md-4"
                }>
                  <div className="form-group">
                    <label>Subject</label>
                    <select
                      className="form-control"
                      value={filters.subject_id}
                      onChange={(e) => setFilters(prev => ({ ...prev, subject_id: e.target.value }))}
                      disabled={!filters.franchise_id}
                    >
                      <option value="">All Subjects</option>
                      {subjects.map(subject => (
                        subject.subject_id && (
                          <option key={subject.subject_id} value={subject.subject_id.toString()}>
                            {subject.name}
                          </option>
                        )
                      ))}
                    </select>
                  </div>
                </div>

                <div className={
                  franchises.length === 1 && centres.length === 1 ? "col-md-4" :
                  franchises.length > 1 && centres.length > 1 ? "col-md-2" :
                  franchises.length > 1 || centres.length > 1 ? "col-md-3" : "col-md-4"
                }>
                  <div className="form-group">
                    <label>&nbsp;</label>
                    <button
                      type="button"
                      className="btn btn-primary btn-block"
                      onClick={fetchSessionNotes}
                      disabled={loading || !filters.session_date}
                    >
                      {loading ? (
                        <>
                          <i className="fas fa-spinner fa-spin mr-2"></i>
                          Loading...
                        </>
                      ) : (
                        <>
                          <i className="fas fa-search mr-2"></i>
                          Search
                        </>
                      )}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Session Notes Table */}
          {sessionNotes.length > 0 && (
            <div className="card">
              <div className="card-header">
                <h3 className="card-title">
                  Session Notes Pending Review ({sessionNotes.length})
                </h3>
                <div className="card-tools">
                  <span className="badge badge-info mr-2">
                    {sessionNotes.filter(n => n.selected).length} selected for publishing
                  </span>
                  <span className="badge badge-warning">
                    {sessionNotes.filter(n => !n.selected).length} to be drafted
                  </span>
                </div>
              </div>
              <div className="card-body">
                <div className="table-responsive">
                  <table className="table table-bordered table-hover table-sm mb-0" style={{ fontSize: '14px' }}>
                    <thead style={{ backgroundColor: '#f8f9fa' }}>
                      <tr>
                        <th style={{ width: '40px', padding: '8px 4px' }} className="text-center">
                          <input
                            type="checkbox"
                            checked={isAllSelected}
                            ref={(input) => {
                              if (input) {
                                input.indeterminate = isSomeSelected;
                              }
                            }}
                            onChange={(e) => handleSelectAll(e.target.checked)}
                            style={{ width: '16px', height: '16px', cursor: 'pointer' }}
                            title={isAllSelected ? 'Unselect all' : 'Select all'}
                          />
                        </th>
                        <th style={{ width: '40px', padding: '8px 4px' }}>Sr.</th>
                        <th style={{ width: '180px', padding: '8px' }}>Student</th>
                        <th style={{ width: '120px', padding: '8px' }}>Subject</th>
                        <th style={{ width: '70px', padding: '8px 4px', textAlign: 'center' }}>Attend</th>
                        <th style={{ width: '130px', padding: '8px 4px', textAlign: 'center' }}>Homework</th>
                        <th style={{ padding: '8px' }}>Session Note</th>
                        <th style={{ width: '140px', padding: '8px' }}>Created By</th>
                      </tr>
                    </thead>
                    <tbody>
                      {sessionNotes.map((note, index) => {
                        const homeworkStatus = getHomeworkStatus(note.homework_status);
                        
                        return (
                          <tr key={note.session_note_student_id} className={note.selected ? 'table-success' : ''}>
                            <td className="text-center" style={{ padding: '6px 4px' }}>
                              <input
                                type="checkbox"
                                checked={note.selected}
                                onChange={(e) => handleSelectNote(note.session_note_student_id, e.target.checked)}
                                style={{ width: '16px', height: '16px', cursor: 'pointer' }}
                              />
                            </td>
                            <td style={{ padding: '6px 4px' }}>{index + 1}</td>
                            <td style={{ padding: '6px 8px' }}>{note.student_name}</td>
                            <td style={{ padding: '6px 8px' }}>{note.subject_name}</td>
                            <td className="text-center" style={{ padding: '6px 4px' }}>
                              <span className={`badge ${note.attended === 'yes' ? 'badge-success' : 'badge-danger'}`} style={{ fontSize: '10px', padding: '2px 5px' }}>
                                {note.attended === 'yes' ? 'Yes' : 'No'}
                              </span>
                            </td>
                            <td className="text-center" style={{ padding: '6px 4px' }}>
                              <span className={`badge ${homeworkStatus.class}`} style={{ fontSize: '10px', padding: '2px 5px' }}>
                                {homeworkStatus.text}
                              </span>
                            </td>
                            <td style={{ padding: '6px 8px', fontSize: '12px' }}>
                              <textarea
                                className="form-control form-control-sm"
                                rows={3}
                                value={note.edited_session_note !== undefined ? note.edited_session_note : note.session_note}
                                onChange={(e) => handleSessionNoteChange(note.session_note_student_id, e.target.value)}
                                placeholder="Enter session note..."
                                style={{ fontSize: '12px', minWidth: '300px' }}
                              />
                            </td>
                            <td style={{ padding: '6px 8px' }}>{note.created_by_name || '--'}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
              
              {/* General Comment for Unselected Notes */}
              {sessionNotes.filter(n => !n.selected).length > 0 && (
                <div className="card-body border-top pt-3 pb-3">
                  <div className="form-group mb-0">
                    <label style={{ fontSize: '14px', fontWeight: 600 }}>
                      Comment for Drafted Notes ({sessionNotes.filter(n => !n.selected).length})
                      <span className="text-danger">*</span>
                    </label>
                    <textarea
                      className={`form-control form-control-sm ${sessionNotes.filter(n => !n.selected).length > 0 && (!generalComment || generalComment.trim() === '') ? 'is-invalid' : ''}`}
                      rows={2}
                      value={generalComment}
                      onChange={(e) => handleGeneralCommentChange(e.target.value)}
                      placeholder="Enter reason for sending these notes back to draft..."
                      style={{ fontSize: '14px' }}
                    />
                    {sessionNotes.filter(n => !n.selected).length > 0 && (!generalComment || generalComment.trim() === '') && (
                      <div className="invalid-feedback d-block" style={{ fontSize: '12px' }}>
                        Comment required for drafted notes
                      </div>
                    )}
                  </div>
                </div>
              )}
              
              <div className="card-footer">
                <button
                  type="button"
                  className="btn btn-success mr-2"
                  onClick={() => {
                    const selectedCount = sessionNotes.filter(n => n.selected).length;
                    const unselectedCount = sessionNotes.filter(n => !n.selected).length;
                    const editedCount = sessionNotes.filter(n => n.edited_session_note !== undefined && n.edited_session_note !== n.session_note).length;
                    
                    confirmAlert({
                      title: 'Confirm Review Submission',
                      message: 'dummy', // Will be replaced by customUI
                      customUI: ({ onClose }) => {
                        return (
                          <div className="custom-ui" style={{ 
                            padding: '30px', 
                            backgroundColor: 'white', 
                            borderRadius: '10px',
                            boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
                            minWidth: '400px'
                          }}>
                            <h2 style={{ marginBottom: '20px', fontSize: '24px', fontWeight: 600 }}>
                              Confirm Review Submission
                            </h2>
                            <p style={{ marginBottom: '20px', fontSize: '16px', color: '#666' }}>
                              Are you sure you want to process this review?
                            </p>
                            <div style={{ marginBottom: '25px' }}>
                              <div style={{ 
                                display: 'flex', 
                                alignItems: 'center', 
                                marginBottom: '12px',
                                padding: '10px',
                                backgroundColor: '#d4edda',
                                borderRadius: '5px'
                              }}>
                                <i className="fas fa-check-circle" style={{ color: '#28a745', fontSize: '20px', marginRight: '10px' }}></i>
                                <span style={{ fontSize: '15px', fontWeight: 500 }}>
                                  <strong style={{ color: '#28a745' }}>{selectedCount}</strong> note(s) will be <strong>published</strong>
                                </span>
                              </div>
                              <div style={{ 
                                display: 'flex', 
                                alignItems: 'center', 
                                marginBottom: '12px',
                                padding: '10px',
                                backgroundColor: '#f8f9fa',
                                borderRadius: '5px'
                              }}>
                                <i className="fas fa-file" style={{ color: '#6c757d', fontSize: '20px', marginRight: '10px' }}></i>
                                <span style={{ fontSize: '15px', fontWeight: 500 }}>
                                  <strong style={{ color: '#6c757d' }}>{unselectedCount}</strong> note(s) will be saved as <strong>draft</strong>
                                </span>
                              </div>
                              {editedCount > 0 && (
                                <div style={{ 
                                  display: 'flex', 
                                  alignItems: 'center',
                                  padding: '10px',
                                  backgroundColor: '#fff3cd',
                                  borderRadius: '5px'
                                }}>
                                  <i className="fas fa-edit" style={{ color: '#ffc107', fontSize: '20px', marginRight: '10px' }}></i>
                                  <span style={{ fontSize: '15px', fontWeight: 500 }}>
                                    <strong style={{ color: '#856404' }}>{editedCount}</strong> note(s) have been <strong>edited</strong>
                                  </span>
                                </div>
                              )}
                            </div>
                            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
                              <button
                                className="btn btn-secondary"
                                onClick={onClose}
                                style={{ minWidth: '100px' }}
                              >
                                Cancel
                              </button>
                              <button
                                className="btn btn-success"
                                onClick={() => {
                                  handleSubmit();
                                  onClose();
                                }}
                                style={{ minWidth: '100px' }}
                              >
                                Yes, Process
                              </button>
                            </div>
                          </div>
                        );
                      }
                    });
                  }}
                  disabled={submitting || sessionNotes.length === 0}
                >
                  {submitting ? (
                    <>
                      <i className="fas fa-spinner fa-spin mr-2"></i>
                      Processing...
                    </>
                  ) : (
                    <>
                      <i className="fas fa-check mr-2"></i>
                      Process Review ({sessionNotes.filter(n => n.selected).length} to publish, {sessionNotes.filter(n => !n.selected).length} to draft)
                    </>
                  )}
                </button>
                <Link to="/session-notes" className="btn btn-default">
                  <i className="fas fa-arrow-left mr-1"></i>
                  Back to List
                </Link>
              </div>
            </div>
          )}

          {/* Empty State - No filters selected */}
          {!loading && sessionNotes.length === 0 && !filters.session_date && (
            <div className="card">
              <div className="card-body">
                <div className="text-center text-muted py-5">
                  <i className="fas fa-filter fa-3x mb-3"></i>
                  <p>Please select a session date to view session notes for review</p>
                  <small>Apply filters to get started</small>
                </div>
              </div>
            </div>
          )}

          {/* Empty State - Filters applied but no results */}
          {!loading && sessionNotes.length === 0 && filters.session_date && (
            <div className="card">
              <div className="card-body">
                <div className="text-center text-muted py-5">
                  <i className="fas fa-clipboard-check fa-3x mb-3"></i>
                  <p>No session notes pending review for the selected criteria</p>
                  <small>Try adjusting your filters or select a different date</small>
                </div>
              </div>
            </div>
          )}
        </div>
      </section>
    </div>
  );
};

export default ReviewSessionNotes;
